/**
 * VH-85: Add a recipe version to the meal plan.
 *
 * The headline UX for VH-85 — answers the user's question
 * "how do I add my cookie to my meal plan?".
 *
 * Calls POST /api/v1/recipes/{recipe_id}/versions/{version_id}/add-to-meal-plan
 * with body { log_date, meal_type, servings_count }.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Dialog } from '@headlessui/react'
import { Link } from 'react-router-dom'
import {
  XMarkIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { recipesAPI } from '../../services/recipes'

function localDateString(d = new Date()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'other']

export default function AddToMealPlanDialog({
  isOpen,
  onClose,
  recipeId,
  versionId,
  versionServings = 1,
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [logDate, setLogDate] = useState(localDateString())
  const [mealType, setMealType] = useState('lunch')
  const [servingsCount, setServingsCount] = useState(versionServings || 1)
  const [error, setError] = useState('')
  const [successMeal, setSuccessMeal] = useState(null)

  useEffect(() => {
    if (isOpen) {
      setLogDate(localDateString())
      setMealType('lunch')
      setServingsCount(versionServings || 1)
      setError('')
      setSuccessMeal(null)
    }
  }, [isOpen, versionServings])

  const addMutation = useMutation({
    mutationFn: () =>
      recipesAPI
        .addToMealPlan(recipeId, versionId, {
          log_date: logDate,
          meal_type: mealType,
          servings_count: parseFloat(servingsCount) || 1,
        })
        .then((r) => r.data),
    onSuccess: (data) => {
      setSuccessMeal(data)
      // Invalidate any meal / nutrition queries that may be visible.
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : t('recipes.addToMealPlan.failed'))
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    const s = parseFloat(servingsCount)
    if (!s || s <= 0) {
      setError(t('recipes.addToMealPlan.failed'))
      return
    }
    addMutation.mutate()
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md bg-surface-container rounded-lg shadow-xl">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold flex items-center gap-2">
              <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
              {t('recipes.addToMealPlan.title')}
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              aria-label={t('common.cancel')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {!successMeal && (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipes.addToMealPlan.logDate')}
                </label>
                <input
                  type="date"
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                  className="form-input w-full"
                  required
                  data-testid="add-to-mealplan-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipes.addToMealPlan.mealType')}
                </label>
                <select
                  value={mealType}
                  onChange={(e) => setMealType(e.target.value)}
                  className="form-select w-full"
                  data-testid="add-to-mealplan-meal-type"
                >
                  {MEAL_TYPES.map((m) => (
                    <option key={m} value={m}>
                      {t(
                        `recipes.addToMealPlan.mealType${
                          m.charAt(0).toUpperCase() + m.slice(1)
                        }`
                      )}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('recipes.addToMealPlan.servingsCount')}
                </label>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={servingsCount}
                  onChange={(e) => setServingsCount(e.target.value)}
                  className="form-input w-full"
                  required
                  data-testid="add-to-mealplan-servings"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('recipes.addToMealPlan.servingsHelp', {
                    total: versionServings,
                  })}
                </p>
              </div>
              {error && (
                <div className="text-sm text-red-600" role="alert">
                  {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={addMutation.isPending}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={addMutation.isPending}
                  data-testid="add-to-mealplan-submit"
                >
                  {addMutation.isPending
                    ? t('recipes.addToMealPlan.submitting')
                    : t('recipes.addToMealPlan.submit')}
                </button>
              </div>
            </form>
          )}

          {successMeal && (
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-md border border-green-200">
                <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">
                    {t('recipes.addToMealPlan.success')}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    {successMeal.log_date} — {successMeal.meal_type} —{' '}
                    {Math.round(successMeal.total_calories)} kcal
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Link
                  to={`/nutrition?date=${successMeal.log_date}`}
                  className="btn-secondary"
                  onClick={onClose}
                  data-testid="add-to-mealplan-view"
                >
                  {t('recipes.addToMealPlan.viewMealPlan')}
                </Link>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-primary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
